import type {RefObject} from 'react'
import type React from 'react'
import {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react'

import {DisplayMode, VALUES} from '..'
import {useSessionStorage} from '@github-ui/use-safe-storage'
import type {IssueFormRef} from '@github-ui/issue-form'

export type IssueCreateMoreCreatedPath = {
  owner: string
  repo: string
  number: number | undefined
}

export type IssueCreateProviderProps = {
  storageKeyPrefix: string
  defaultDisplayMode?: DisplayMode
  children: React.ReactNode
}

type IssueCreateOnCreateAction = {
  onCreate: (isSubmitting: boolean, createMore: boolean) => void
}

type IssueCreateContextState = {
  createMore: boolean
  setCreateMore: (value: boolean) => void

  createMoreCreatedPath: IssueCreateMoreCreatedPath
  setCreateMoreCreatedPath: (value: IssueCreateMoreCreatedPath) => void

  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void

  displayMode: DisplayMode
  setDisplayMode: (value: DisplayMode) => void
  resetDisplayMode: () => void

  onCreateAction: RefObject<IssueCreateOnCreateAction>
  issueFormRef: RefObject<IssueFormRef>

  storageKeyPrefix: string
}

const IssueCreateContext = createContext<IssueCreateContextState | null>(null)

export function IssueCreateContextProvider({
  storageKeyPrefix,
  defaultDisplayMode = DisplayMode.TemplatePicker,
  children,
}: IssueCreateProviderProps) {
  const {issueCreateMore} = VALUES.localStorageKeys

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createMore, setCreateMore] = useSessionStorage<boolean>(issueCreateMore(storageKeyPrefix), false)
  const [createMoreCreatedPath, setCreateMoreCreatedPath] = useState<IssueCreateMoreCreatedPath>({
    owner: '',
    repo: '',
    number: undefined,
  })
  const [displayMode, setDisplayMode] = useState(defaultDisplayMode)
  const resetDisplayMode = useCallback(() => setDisplayMode(defaultDisplayMode), [defaultDisplayMode])

  const onCreateAction = useRef<IssueCreateOnCreateAction>(null)
  const issueFormRef = useRef<IssueFormRef>(null)

  const contextValue: IssueCreateContextState = useMemo(() => {
    return {
      storageKeyPrefix,
      createMore,
      setCreateMore,
      createMoreCreatedPath,
      setCreateMoreCreatedPath,
      displayMode,
      setDisplayMode,
      resetDisplayMode,
      isSubmitting,
      setIsSubmitting,
      onCreateAction,
      issueFormRef,
    }
  }, [
    storageKeyPrefix,
    createMore,
    setCreateMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    displayMode,
    resetDisplayMode,
    isSubmitting,
    issueFormRef,
  ])

  return <IssueCreateContext.Provider value={contextValue}>{children}</IssueCreateContext.Provider>
}

export const useIssueCreateContext = () => {
  const context = useContext(IssueCreateContext)
  if (!context) {
    throw new Error('useIssueCreateContext must be used within a IssueCreateContextProvider.')
  }

  return context
}

try{ IssueCreateContext.displayName ||= 'IssueCreateContext' } catch {}
try{ IssueCreateContextProvider.displayName ||= 'IssueCreateContextProvider' } catch {}